import {
  Alert,
  Button,
  Details,
  Dialog,
  FileDropZone,
  LinkButton,
  readJson,
  TextArea,
  useActionKey,
} from "@emdur/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useApi } from "../lib/apiContext";
import {
  type CapabilityItem,
  type Client,
  type ClientManifest,
  appHref,
  clientsPath,
  diffClients,
  endUserSignupText,
  parseManifest,
  permissionsOf,
  manifestJson,
} from "../lib/clients";
import { errorText } from "../lib/errors";
import { permissionPhrase } from "../lib/permissionCopy";
import { download, serviceClientKey, type Jwk } from "../lib/keys";
import { type Proposal, proposalHref } from "../lib/proposals";
import { PermissionDiff } from "./Permissions";
import {
  diffPermissions,
  roleHref,
  rolesPath,
  type Role,
  type RoleCreate,
} from "../lib/roles";
import { useRegistry } from "./RegistryProvider";
import { AgentPermissions } from "./AgentPermissions";
import { PublicKeyIds } from "./PublicKeyIds";
import { PermissionRefreshAction } from "./PermissionRefreshAction";
import {
  type Agent,
  type AgentCreate,
  agentExpiry,
  agentHref,
  agentPermissionPhrase,
  agentsPath,
} from "../lib/agents";
import {
  consumeAgentVerification,
  pendingAgentVerification,
  startAgentVerification,
  verificationHash,
} from "../lib/auth";

export function ManifestRegistration({
  org,
  catalogue,
  onClose,
  agent = false,
  role = false,
}: {
  org: string;
  catalogue: CapabilityItem[];
  onClose: () => void;
  agent?: boolean;
  role?: boolean;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const keys = useActionKey();
  const registry = useRegistry();
  const [text, setText] = useState(() => {
    const proof = agent ? pendingAgentVerification(org) : null;
    return proof?.action === "create-agent"
      ? JSON.stringify(proof.body, null, 2)
      : "";
  });
  const [manifest, setManifest] = useState<ClientManifest | null>(null);
  const [roleManifest, setRoleManifest] = useState<RoleCreate | null>(null);
  const [privateKey, setPrivateKey] = useState<Jwk | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState("");
  const [verification] = useState(() =>
    agent ? pendingAgentVerification(org) : null,
  );
  const [agentManifest, setAgentManifest] = useState<AgentCreate | null>(() =>
    verification?.action === "create-agent"
      ? (verification.body as AgentCreate)
      : null,
  );
  const attempted = useRef(false);
  async function createAgent(automatic = false) {
    if (!agentManifest || keys.sending.current) return;
    keys.sending.current = true;
    setBusy(true);
    setError("");
    try {
      if (!registry.personId || registry.error)
        throw new Error(
          "Your identity could not be checked. Reload and try again.",
        );
      const { parseAgentManifest } = await import("../lib/agentManifest");
      const body = parseAgentManifest(
        JSON.stringify(agentManifest),
        registry.personId,
      );
      const argumentsHash = await verificationHash({
        org,
        action: "create-agent",
        body,
      });
      const intent = {
        action: "create-agent" as const,
        org,
        personId: registry.personId,
        expiresAt: Date.now() + 300000,
        body,
        argumentsHash,
        key:
          verification?.argumentsHash === argumentsHash &&
          verification.personId === registry.personId
            ? verification.key
            : keys.keyFor("create-agent", body),
      };
      if (!consumeAgentVerification(intent)) {
        if (automatic)
          throw new Error(
            "The reviewed manifest or signed-in person changed. Review it and create again.",
          );
        await startAgentVerification(intent);
        return;
      }
      const answer = await readJson<Agent>(api.fetch, agentsPath(org), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": intent.key,
        },
        body: JSON.stringify(body),
      });
      keys.spent("create-agent", intent.key);
      await registry.reload();
      void navigate(`${agentHref(org, answer.data.id)}?created`);
      onClose();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }
  useEffect(() => {
    if (
      verification?.action === "create-agent" &&
      !registry.loading &&
      !attempted.current
    ) {
      attempted.current = true;
      void createAgent(true);
    }
  }, [registry.loading, verification]);
  async function review() {
    setError("");
    setBusy(true);
    try {
      if (role) {
        const { parseRoleManifest } = await import("../lib/roles");
        setRoleManifest(parseRoleManifest(text, catalogue));
        return;
      }
      if (agent) {
        if (!registry.personId || registry.error)
          throw new Error(
            "Your identity could not be checked. Reload and try again.",
          );
        const { parseAgentManifest } = await import("../lib/agentManifest");
        const checked = parseAgentManifest(text, registry.personId);
        if (
          checked.capabilities.some(
            (capability) =>
              !catalogue.some((item) => item.capability === capability),
          )
        )
          throw new Error(
            "A permission is unavailable in this organisation. Ask the agent's team to correct the manifest.",
          );
        setAgentManifest(checked);
        return;
      }
      const body = manifestJson(text);
      const candidate = body as Partial<ClientManifest> | null;
      // Service-only manifests may ask the browser to supply their key. A web app always brings server public keys.
      if (
        candidate?.grant_types?.length === 1 &&
        candidate.grant_types[0] === "client_credentials" &&
        !candidate.jwks &&
        !candidate.jwks_uri
      ) {
        const key = await serviceClientKey();
        const checked = parseManifest(
          JSON.stringify({ ...candidate, jwks: key.publicJwks }),
        );
        setPrivateKey(key.privateJwk);
        setManifest(checked);
      } else {
        setPrivateKey(null);
        setManifest(parseManifest(text));
      }
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }
  async function register() {
    const body = roleManifest ?? manifest;
    if (!body || keys.sending.current) return;
    keys.sending.current = true;
    setBusy(true);
    setError("");
    const key = keys.keyFor("register", body);
    try {
      const answer = await readJson<Client | Role | Proposal>(
        api.fetch,
        roleManifest ? rolesPath(org) : clientsPath(org),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": key,
          },
          body: JSON.stringify(body),
        },
      );
      keys.spent("register", key);
      if (privateKey && manifest && "id" in answer.data) {
        download(`${manifest.client_name}.private.jwk.json`, {
          ...privateKey,
          ...(answer.status === 202
            ? { proposal_id: answer.data.id }
            : { client_id: answer.data.id }),
        });
        setPrivateKey(null);
      }
      await registry.reload();
      if (answer.status === 202) {
        setProposal(proposalHref(org, (answer.data as Proposal).id));
        return;
      }
      void navigate(
        "key" in answer.data
          ? roleHref(org, answer.data.key)
          : appHref(org, answer.data.id),
        {
          viewTransition:
            typeof document.startViewTransition === "function" &&
            !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
        },
      );
      onClose();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      keys.sending.current = false;
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={
        role
          ? roleManifest
            ? "Review role access"
            : "Create from a manifest"
          : agent
            ? agentManifest
              ? "Review agent access"
              : "Create from a manifest"
            : manifest
              ? "Review app registration"
              : "Register from a manifest"
      }
      isOpen
      onOpenChange={(open) => !open && !busy && onClose()}
    >
      <div className="flex flex-col gap-5">
        {proposal ? (
          <Alert title="Sent for approval" tone="positive">
            <LinkButton href={proposal}>Open proposal</LinkButton>
          </Alert>
        ) : roleManifest || agentManifest || manifest ? (
          <>
            <h2 className="decision-sentence">
              {roleManifest
                ? `${roleManifest.name} can ${permissionPhrase(roleManifest.capabilities, catalogue)}`
                : agentManifest
                  ? `${agentManifest.name} will be able to ${agentPermissionPhrase(agentManifest, catalogue)}`
                  : manifest && (
                      <>
                        {manifest.client_name} wants to{" "}
                        {permissionPhrase(permissionsOf(manifest), catalogue)}
                      </>
                    )}
            </h2>
            {roleManifest ? (
              <>
                <Details
                  items={[
                    ["Role", roleManifest.name],
                    ["Key", roleManifest.key],
                    ...(roleManifest.description
                      ? [
                          ["Purpose", roleManifest.description] as [
                            string,
                            string,
                          ],
                        ]
                      : []),
                  ]}
                />
                <PermissionDiff
                  diff={diffPermissions([], roleManifest.capabilities)}
                  catalogue={catalogue}
                  subject="role"
                />
                <p className="text-sm text-muted">
                  Anyone assigned this role receives these permissions.
                </p>
              </>
            ) : agentManifest ? (
              <>
                <Details
                  items={[
                    [
                      "Kind",
                      agentManifest.kind === "external"
                        ? "External agent"
                        : "AI twin",
                    ],
                    [
                      "Sponsored by",
                      agentManifest.sponsors
                        .map(
                          (person) =>
                            registry.sponsors[person] ??
                            "A sponsor whose name is unavailable",
                        )
                        .join(", "),
                    ],
                    ["Expiry", agentExpiry(agentManifest.expires_at)],
                    [
                      "Keys",
                      agentManifest.jwks_uri ? (
                        <span className="break-all">
                          {agentManifest.jwks_uri}
                        </span>
                      ) : agentManifest.jwks ? (
                        <PublicKeyIds keys={agentManifest.jwks.keys} />
                      ) : (
                        <span className="break-all">
                          Existing client {agentManifest.client_id}
                        </span>
                      ),
                    ],
                  ]}
                />
                <AgentPermissions
                  agent={agentManifest}
                  catalogue={catalogue}
                  review
                />
                {agentManifest.disclosure?.limitations && (
                  <p className="text-sm text-muted">
                    {agentManifest.disclosure.limitations}
                  </p>
                )}
                <p className="text-sm text-muted">
                  Verify with your passkey to grant this access. The manifest
                  contains public keys only.
                </p>
              </>
            ) : (
              manifest && (
                <>
                  <Details
                    items={[
                      ["App", manifest.client_name],
                      [
                        "Sign-in",
                        manifest.grant_types.includes("authorization_code")
                          ? "People sign in to this app"
                          : "A service acts for your organisation",
                      ],
                      [
                        "Consent",
                        manifest.first_party
                          ? "Signs people in without a consent screen"
                          : "People review consent when required",
                      ],
                      [
                        "Keys",
                        manifest.jwks_uri ? (
                          <span className="break-all">{manifest.jwks_uri}</span>
                        ) : manifest.jwks?.keys.length ? (
                          <PublicKeyIds keys={manifest.jwks.keys} />
                        ) : (
                          "Device-bound sign-in"
                        ),
                      ],
                      ...(manifest.end_user_signup
                        ? [
                            [
                              "End-user sign-up",
                              <span className="wrap-anywhere">
                                {endUserSignupText(manifest.end_user_signup)}
                              </span>,
                            ] as [string, ReactNode],
                          ]
                        : []),
                    ]}
                  />
                  <PermissionDiff
                    diff={diffClients(
                      {
                        ...manifest,
                        scope: "",
                        capabilities: [],
                        redirect_uris: [],
                      },
                      manifest,
                    )}
                    catalogue={catalogue}
                    reasons={manifest["x-emdur-reasons"]}
                  />
                  {privateKey && (
                    <Alert tone="warning">
                      A service key was generated in this browser. Its private
                      key will download once when you register. Keep that file
                      safe.
                    </Alert>
                  )}
                </>
              )
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                isLoading={busy}
                onPress={() => void (agent ? createAgent() : register())}
              >
                {role ? "Create role" : agent ? "Create agent" : "Register app"}
              </Button>
              <Button
                isDisabled={busy}
                onPress={() => {
                  setManifest(null);
                  setPrivateKey(null);
                  setAgentManifest(null);
                  setRoleManifest(null);
                }}
              >
                Back to manifest
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted">
              Paste or drop the JSON manifest from your{" "}
              {role ? "role" : agent ? "agent" : "app"}.{" "}
              {role
                ? "It already names the role, its key and its permissions."
                : "It already names it, its permissions and its public keys."}
            </p>
            <FileDropZone
              label="Manifest file"
              accept={["application/json"]}
              limitBytes={65536}
              onFile={(file) => {
                void file
                  .text()
                  .then(setText, () =>
                    setError(
                      "This file could not be read. Try pasting its contents.",
                    ),
                  );
              }}
              hint="One JSON file, up to 64 KB."
            />
            <TextArea
              label={
                role
                  ? "Role manifest"
                  : agent
                    ? "Agent manifest"
                    : "App manifest"
              }
              value={text}
              onChange={setText}
              rows={8}
            />
            <Button
              variant="primary"
              isLoading={busy}
              isDisabled={!text.trim() || !catalogue.length}
              onPress={() => void review()}
            >
              Review manifest
            </Button>
          </>
        )}
        {error && (
          <Alert tone="danger">
            {error}
            <PermissionRefreshAction when={error} />
          </Alert>
        )}
      </div>
    </Dialog>
  );
}
